import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Avatar,
  Stack
} from '@mui/material';
import { 
  EmojiEvents, 
  MonetizationOn,
  Star,
  BarChart,
  SwapHoriz,
  AutoGraph,
  Verified
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const PrizesPage = () => {
  const xmTopPerformers = [
    {
      rank: "1st",
      prize: "R250,000",
      color: "#FFD700",
      icon: <EmojiEvents sx={{ fontSize: 70, color: "#FFD700", filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.5))', stroke: '#000000', strokeWidth: 1 }} />,
      gradient: "linear-gradient(135deg, #FFD700 0%, #FFA000 100%)",
      textColor: "#000000"
    },
    {
      rank: "2nd", 
      prize: "R100,000",
      color: "#C0C0C0",
      icon: <EmojiEvents sx={{ fontSize: 70, color: "#E8E8E8", filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.7))', border: '2px solid #999999', borderRadius: '50%' }} />,
      gradient: "linear-gradient(135deg, #C0C0C0 0%, #9E9E9E 100%)",
      textColor: "#000000"
    },
    {
      rank: "3rd",
      prize: "R50,000", 
      color: "#CD7F32",
      icon: <EmojiEvents sx={{ fontSize: 70, color: "#D2691E", filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.6))' }} />,
      gradient: "linear-gradient(135deg, #CD7F32 0%, #A0522D 100%)",
      textColor: "#FFFFFF"
    }
  ];

  const xmPercentageGrowth = [
    { rank: "1st", prize: "R50,000", color: "#2E7D32", textColor: "#FFFFFF" },
    { rank: "2nd", prize: "R30,000", color: "#388E3C", textColor: "#FFFFFF" },
    { rank: "3rd", prize: "R20,000", color: "#689F38", textColor: "#FFFFFF" }
  ];

  const weltradeTopTraders = [
    { rank: "1st", prize: "R70,000", color: "#1565C0", textColor: "#FFFFFF" },
    { rank: "2nd", prize: "R40,000", color: "#1976D2", textColor: "#FFFFFF" },
    { rank: "3rd", prize: "R20,000", color: "#0288D1", textColor: "#FFFFFF" }
  ];

  return (
    <Box sx={{ pt: 12, pb: 8, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box textAlign="center" mb={6}>
            <Typography variant="h2" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}>
              FX Wars Prize Pool
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Compete for substantial cash prizes in the ultimate forex trading competition. 
              Trade smart, earn big, and claim your share of the prize pool!
            </Typography>
          </Box>
        </motion.div>

        {/* XM Top 10 Points Accumulated */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Box mb={8}>
            <Paper elevation={4} sx={{ p: 4, borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <Box display="flex" alignItems="center" justifyContent="center" mb={4}>
                <img 
                  src="/partnersLogo-15years.svg" 
                  alt="XM Global Logo" 
                  style={{ 
                    width: '50px', 
                    height: 'auto', 
                    marginRight: '15px'
                  }} 
                />
                <Typography variant="h3" fontWeight="bold">
                  XM Global - Top 10
                </Typography>
              </Box>
              <Typography variant="h6" textAlign="center" mb={4} sx={{ opacity: 0.9 }}>
                Most Points Accumulated Competition
              </Typography>
              
              {/* Top 3 */}
              <Grid container spacing={4} mb={4}>
                {xmTopPerformers.map((performer, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Card sx={{ 
                      height: '100%',
                      background: performer.gradient,
                      color: 'white',
                      border: `3px solid ${performer.color}`,
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 15px 30px ${performer.color}40`
                      }
                    }}>
                      <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
                          {performer.icon}
                          {performer.rank === "1st" && (
                            <Box sx={{
                              position: 'absolute',
                              top: -5,
                              left: -5,
                              right: -5,
                              bottom: -5,
                              background: 'radial-gradient(circle, rgba(255,215,0,0.3) 0%, transparent 70%)',
                              borderRadius: '50%',
                              zIndex: -1
                            }} />
                          )}
                        </Box>
                        <Typography variant="h4" fontWeight="bold" sx={{ mt: 2, mb: 1, color: performer.textColor }}>
                          {performer.rank} Place
                        </Typography>
                        <Typography variant="h3" fontWeight="bold" sx={{ 
                          textShadow: performer.textColor === '#000000' ? '1px 1px 2px rgba(255,255,255,0.7)' : '2px 2px 4px rgba(0,0,0,0.3)',
                          color: performer.textColor
                        }}>
                          {performer.prize}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* 4th to 10th Places */}
              <Card sx={{ 
                background: 'rgba(255,255,255,0.1)', 
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight="bold" textAlign="center" mb={3}>
                    4th to 10th Place Winners
                  </Typography>
                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Box sx={{ 
                      p: 3, 
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: 2,
                      textAlign: 'center',
                      border: '1px solid rgba(255,255,255,0.2)',
                      minWidth: '200px'
                    }}>
                  
                      <Typography variant="h5" color="#FFEB3B" fontWeight="bold">
                        R20,000
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                        Each
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Paper>
          </Box>
        </motion.div>

        {/* XM Percentage Growth */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box mb={8}>
            <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="center" mb={4}>
                <img 
                  src="/partnersLogo-15years.svg" 
                  alt="XM Global Logo" 
                  style={{ 
                    width: '50px', 
                    height: 'auto', 
                    marginRight: '15px'
                  }} 
                />
                <Typography variant="h3" fontWeight="bold" sx={{ color: '#1B5E20' }}>
                  XM Global Most Percentage Growth
                </Typography>
              </Box>
              <Typography variant="h6" textAlign="center" mb={4} color="text.secondary">
                Top 3 Highest Percentage Returns
              </Typography>
              
              <Grid container spacing={4}>
                {xmPercentageGrowth.map((winner, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Card sx={{ 
                      height: '100%',
                      background: `linear-gradient(135deg, ${winner.color} 0%, ${winner.color}CC 100%)`,
                      color: 'white',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      }
                    }}>
                      <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <AutoGraph sx={{ fontSize: 60, mb: 2, filter: 'drop-shadow(1px 1px 3px rgba(0,0,0,0.4))' }} />
                        <Typography variant="h5" fontWeight="bold" mb={1} sx={{ color: winner.textColor }}>
                          {winner.rank} Place
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: winner.textColor }}>
                          {winner.prize}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ 
                mt: 4, 
                p: 3, 
                background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                borderRadius: 2,
                color: 'white',
                textAlign: 'center'
              }}>
                <Verified sx={{ fontSize: 30, mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  *Participants must enter the competition on the XM Global Platform to be eligible
                </Typography>
              </Box>
            </Paper>
          </Box>
        </motion.div>

        {/* Weltrade Most Lots Traded */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Box mb={8}>
            <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="center" mb={4}>
                <SwapHoriz sx={{ fontSize: 40, mr: 2, color: '#1565C0' }} />
                <Typography variant="h3" fontWeight="bold" sx={{ color: '#0D47A1' }}>
                  Weltrade Most Lots Traded
                </Typography>
              </Box>
              <Typography variant="h6" textAlign="center" mb={4} color="text.secondary">
                Highest Trading Volume Competition
              </Typography>
              
              <Grid container spacing={4}>
                {weltradeTopTraders.map((trader, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Card sx={{ 
                      height: '100%',
                      background: `linear-gradient(135deg, ${trader.color} 0%, ${trader.color}CC 100%)`,
                      color: 'white',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      }
                    }}>
                      <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <BarChart sx={{ fontSize: 60, mb: 2, filter: 'drop-shadow(1px 1px 3px rgba(0,0,0,0.4))' }} />
                        <Typography variant="h5" fontWeight="bold" mb={1} sx={{ color: trader.textColor }}>
                          {trader.rank} Place
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: trader.textColor }}>
                          {trader.prize}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
        </motion.div>

        {/* Monthly Overall Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box mb={6}>
            <Paper elevation={4} sx={{ 
              p: 4, 
              borderRadius: 3, 
              background: 'linear-gradient(135deg, #E91E63 0%, #9C27B0 100%)',
              color: 'white'
            }}>
              <Box display="flex" alignItems="center" justifyContent="center" mb={4}>
                <img 
                  src="/partnersLogo-15years.svg" 
                  alt="XM Global Logo" 
                  style={{ 
                    width: '50px', 
                    height: 'auto', 
                    marginRight: '15px'
                  }} 
                />
                <Typography variant="h3" fontWeight="bold">
                  Monthly Excellence Award
                </Typography>
              </Box>
              
              <Card sx={{ 
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white'
              }}>
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <MonetizationOn sx={{ fontSize: 80, mb: 2, color: '#FFD700', filter: 'drop-shadow(2px 2px 6px rgba(0,0,0,0.5))' }} />
                  <Typography variant="h4" fontWeight="bold" mb={2}>
                    Best Overall Performance on XM Global
                  </Typography>
                  <Typography variant="h2" fontWeight="bold" sx={{ 
                    color: '#FFD700',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)' 
                  }}>
                    R50,000
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 2, opacity: 0.9 }}>
                    Every Month
                  </Typography>
                </CardContent>
              </Card>
            </Paper>
          </Box>
        </motion.div>

        {/* Footer Note */}
        <Box textAlign="center" mt={6} p={3} sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.9)', 
          borderRadius: 2,
          border: '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          <Typography variant="body1" color="text.secondary" fontWeight="bold">
            <Star sx={{ verticalAlign: 'middle', mr: 1, color: '#FFD700' }} />
            All prizes are subject to competition terms and conditions. 
            Winners will be verified and prizes distributed according to platform rules.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrizesPage; 